import {
  DashboardState,
  EAppSupportType,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import { IMqttActionsDataModel } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ActionDefinitionLookup } from "../../../../shared/utility/action-definition-lookup";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { isOfflineCompatibleActionDefinition } from "../../../../shared/utility/offline-action-compatibility";
import { ActionPanel, EActionPanelStatus, IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { EActionNodeCategory } from "./action-picker";
import type { IActionPickerOption } from "./action-picker-popup/action-picker-node";
import ActionPickerPopup from "./action-picker-popup/action-picker-popup";
import { SymbolPicker } from "./symbol-picker";

export interface IMqttSubscribeActionsSymbolGroupState {
  popupVisible: boolean;
  /** The popup's option tree, built when it opens. */
  options: IActionPickerOption[];
}
export interface IMqttSubscribeActionsSymbolGroupProps {
  action?: IAction;
  mqttSubscribeActionData?: IMqttActionsDataModel;
  updateMqttSubscribeActionsData: (data: IMqttActionsDataModel, changeReason?: ESdChangeReason) => void;
  getActionDefinitionItems: () => CascaderOptionType[];
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol) => void;
}
class MqttSubscribeActionsSymbolGroup extends PureSdComponent<
  IMqttSubscribeActionsSymbolGroupProps & IWithSDProps,
  IMqttSubscribeActionsSymbolGroupState
> {
  constructor(props: IMqttSubscribeActionsSymbolGroupProps & IWithSDProps) {
    super(props);
    this.state = {
      popupVisible: false,
      options: []
    };
  }

  /** Opens and closes the popup. The tree is built on open, so it lists the current catalogue. */
  handlePopupOpenChange = (open: boolean): void => {
    if (open) {
      this.setState({ options: this.props.getActionDefinitionItems(), popupVisible: true });
    } else if (this.state.popupVisible) {
      this.setState({ popupVisible: false });
    }
  };

  /** The popup's pick: the same `selectedOptions` path the Cascader's onChange delivered. */
  handlePick = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddAction(values, selectedOptions);
  };

  actionDefinitionLookup: ActionDefinitionLookup = new ActionDefinitionLookup();

  getActionDefinitionById = (id: string): IActionDefinitionDto | undefined =>
    this.actionDefinitionLookup.get(this.props.sd.actionDefinitions, id);

  handleAdd = (selectedOptions?: CascaderOptionType[] | undefined) => {
    if (!selectedOptions || selectedOptions.length == 0) {
      return;
    }
    const node = selectedOptions.find(
      (o) => o.category === EActionNodeCategory.action || o.category === EActionNodeCategory.triggerEventAction
    );
    if (node === undefined) {
      return;
    }
    let actions = this.props.sd.actionDefinitions;
    const isWebScreen = this.props.sd.getIsWebScreen();
    actions = actions.filter((x) => {
      const { projectType } = this.props.sd.project;
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Web &&
        projectType !== EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs &&
        !isWebScreen
      ) {
        return false;
      }
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Mobile &&
        projectType === EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs
      ) {
        return false;
      }
      if (this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()) {
        if (isOfflineCompatibleActionDefinition(x)) return true;
        return false;
      }
      return true;
    });
    let action: IAction | undefined;
    switch (node.category) {
      case EActionNodeCategory.action:
        action = this.obtainAction(actions, node);
        break;
      case EActionNodeCategory.triggerEventAction:
        action = this.obtainTriggerAction(actions, selectedOptions);
        break;
    }
    if (!action) return;
    this.createAction(action);
  };

  obtainAction(actions: IActionDefinitionDto[], node: CascaderOptionType): IAction | undefined {
    const actionDef = actions.find((p) => p.id === node.value);
    if (!actionDef) return;
    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: actionDef.name,
      parameters: actionDef.actionParameters.map((x) => this.cloneActionParameter(x))
    } as IAction;
  }

  obtainTriggerAction(actions: IActionDefinitionDto[], selectedOptions: CascaderOptionType[]): IAction | undefined {
    const actionDef = actions.find((p) => p.name === "TriggerEvent");
    if (!actionDef) return;
    const targetDiNode = selectedOptions[1];
    const targetEventNode = selectedOptions[2];
    const targetDiIdActionParam = actionDef.actionParameters.find((x) => x.name === "targetDiId");
    const targetEventNameActionParam = actionDef.actionParameters.find((x) => x.name === "targetEventName");
    if (!targetDiIdActionParam || !targetEventNameActionParam) return;
    targetDiIdActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetDiNode.value
    } as ISymbol;
    targetEventNameActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetEventNode.value
    } as ISymbol;
    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: `Trigger [diId:${targetDiNode.value}] > ${targetEventNode.label}`,
      parameters: [targetDiIdActionParam, targetEventNameActionParam]
    } as IAction;
  }

  handleAddAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    this.handleAdd(selectedOptions);
  };

  cloneActionParameter = (actionParameter: IActionParameter) => {
    const tmp = _.cloneDeep(actionParameter);
    return tmp;
  };

  getEmptyData = (): IMqttActionsDataModel => {
    return {
      mqttSubscribeActions: []
    } as IMqttActionsDataModel;
  };

  createAction = (action: IAction) => {
    let mqttActionsData = _.cloneDeep(this.props.mqttSubscribeActionData);
    if (mqttActionsData === undefined) {
      mqttActionsData = this.getEmptyData();
    }
    mqttActionsData.mqttSubscribeActions.push(action);
    this.props.updateMqttSubscribeActionsData(mqttActionsData);
  };

  updateAction = (action: IAction, changeReason?: ESdChangeReason) => {
    let mqttActionsData = _.cloneDeep(this.props.mqttSubscribeActionData);
    if (mqttActionsData === undefined) {
      mqttActionsData = this.getEmptyData();
    }
    mqttActionsData.mqttSubscribeActions = mqttActionsData?.mqttSubscribeActions?.map((x) => {
      if (x.id === action.id) {
        return action;
      }
      return { ...x };
    });
    this.props.updateMqttSubscribeActionsData(mqttActionsData, changeReason);
  };

  deleteAction = (action: IAction) => {
    let mqttActionsData = _.cloneDeep(this.props.mqttSubscribeActionData);
    if (mqttActionsData === undefined) {
      mqttActionsData = this.getEmptyData();
    }
    mqttActionsData.mqttSubscribeActions = mqttActionsData.mqttSubscribeActions.filter((x) => x.id !== action.id);
    this.props.updateMqttSubscribeActionsData(mqttActionsData);
  };

  reorderEventActions = (result: any, sourceIdx: number, destIdx: number) => {
    let mqttActionsData = _.cloneDeep(this.props.mqttSubscribeActionData);
    if (mqttActionsData === undefined) {
      mqttActionsData = this.getEmptyData();
    }
    const [removed] = mqttActionsData.mqttSubscribeActions.splice(sourceIdx, 1);
    mqttActionsData.mqttSubscribeActions.splice(destIdx, 0, removed);
    this.props.updateMqttSubscribeActionsData(mqttActionsData);
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => {
    if (!this.props.updateValues) return;
    this.props.updateValues(value, changeReason);
  };

  getParameterWithName = (name: string): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getActionDefinition = (actionDefinitionId: string | undefined): IActionDefinitionDto | undefined => {
    if (!actionDefinitionId) return undefined;
    return this.props.sd.actionDefinitions.find((x) => x.id === actionDefinitionId);
  };

  getActionDefinitions = (actions: IAction[]): Map<string, IActionDefinitionDto> => {
    const actionDefinitions: Map<string, IActionDefinitionDto> = new Map();

    actions.forEach((action) => {
      const actionDefinition = this.getActionDefinition(action.actionDefinitionId);
      if (actionDefinition && action.actionDefinitionId) {
        actionDefinitions.set(action.actionDefinitionId, actionDefinition);
      }
    });

    return actionDefinitions;
  };

  render() {
    if (this.props.parameters.length <= 0) return null;
    const actionDefinitions = this.getActionDefinitions(this.props.mqttSubscribeActionData?.mqttSubscribeActions ?? []);
    return (
      <div style={{ width: "100%" }}>
        {this.props.parameters.map((parameter, id) => {
          return (
            <div key={id}>
              <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                <ActionParameterTitle parameter={parameter} />
              </span>
              {
                <SymbolPicker
                  action={this.props.action}
                  changeType="onBlur"
                  symbol={parameter.symbol}
                  valueType={parameter.Typename}
                  targetCardinality={parameter.cardinality}
                  onSelectedSymbol={(symbol: ISymbol) => this.props.changeActionParameter(parameter, symbol)}
                  minValue={15}
                  bindingParameter={parameter}
                ></SymbolPicker>
              }
            </div>
          );
        })}
        <div
          style={{
            marginTop: 12,
            marginBottom: 12,
            backgroundColor: "var(--dark-0)"
          }}
        >
          <div className="kuika_symbol-picker__if-else">
            <div className="kuika-right-panel__row">
              <div className="kuika-right-panel__col kuika-right-panel__col--label">
                <span
                  style={{
                    paddingLeft: 6,
                    paddingTop: 10,
                    display: "block",
                    fontWeight: 700,
                    lineHeight: "normal",
                    color: "var(--dark-9)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  Actions
                </span>
              </div>
              <div className="kuika-right-panel__col kuika-right-panel__col--control" style={{ textAlign: "right" }}>
                <div>
                  <ActionPickerPopup
                    projectType={this.props.sd.project?.projectType}
                    options={this.state.options}
                    open={this.state.popupVisible}
                    onOpenChange={this.handlePopupOpenChange}
                    onPick={this.handlePick}
                    getActionDefinition={this.getActionDefinitionById}
                    searchTestId="mqttActionAddActionInput"
                    placeholder="Search any action"
                  >
                    <span
                      data-testid="mqttSubscribeAdtionAddActionButton"
                      className="kuika_addNavigationLink"
                      style={{ paddingRight: 6 }}
                    >
                      + ADD ACTION
                    </span>
                  </ActionPickerPopup>
                </div>
              </div>
            </div>
            <DragDropContext
              onDragEnd={(result) => {
                if (result.destination?.index !== null && result.destination?.index !== undefined) {
                  this.reorderEventActions(result, result.source.index, result.destination.index);
                }
              }}
            >
              <Droppable droppableId={"droppableActions"}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {this.props.mqttSubscribeActionData?.mqttSubscribeActions.map((action, idx) => {
                      return (
                        <Draggable key={`draggable_${action.id}`} draggableId={`draggable_${action.id}`} index={idx}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <ActionPanel
                                eventName="MqttSubscribe"
                                onActionSaved={this.updateAction}
                                actionPanelStatus={EActionPanelStatus.edit}
                                action={_.cloneDeep(action)}
                                actionDefinition={actionDefinitions.get(action.actionDefinitionId!)}
                                deleteAction={this.deleteAction}
                                getActionDefinitionItems={this.props.getActionDefinitionItems}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged ||
      changeReason === ESdChangeReason.RecurringIntervalChange ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}
const mqttSubscribeActionsSymbolGroup = withSD<IMqttSubscribeActionsSymbolGroupProps>(MqttSubscribeActionsSymbolGroup);
export { mqttSubscribeActionsSymbolGroup as MqttSubscribeActionsSymbolGroup };
